import { useEffect, useMemo, useRef, useState } from "react";
import RealQuestionCard from "../QuestionCard";
import "../DemoQuestionsFeed/DemoQuestionsFeed.scss";
import api from "../../api";
import { isLoggedIn } from "../../services/authService";
import { usePopup } from "../PopupManager";
import Swal from "sweetalert2";
import ReplyModal from "../ReplyModal";

const CATEGORY_THEMES = ["teal", "purple", "blue", "coral", "amber"];
const INITIAL_VISIBLE_GROUPS = Number.MAX_SAFE_INTEGER;
const INITIAL_VISIBLE_POSTS = 2;
const INITIAL_VISIBLE_TAGS = 4;

const stripHtml = (value = "") =>
  value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const normalizeTag = (tag) => {
  const clean = `${tag || ""}`.trim().replace(/^#+/, "");
  return clean ? `#${clean}` : "";
};

const splitTags = (tags) => {
  if (Array.isArray(tags)) {
    return tags
      .flatMap((tag) => `${tag || ""}`.split(/[\s,]+/))
      .map(normalizeTag)
      .filter(Boolean);
  }

  if (typeof tags === "string") {
    return tags
      .split(/[\s,]+/)
      .map(normalizeTag)
      .filter(Boolean);
  }

  return [];
};

const normalizeGroupKey = (value = "") =>
  `${value || ""}`.trim().toLowerCase().replace(/\s+/g, " ");

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toTimeValue = (value) => {
  const timestamp = new Date(value || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const timeAgo = (date) => {
  if (!date) return "";

  const sec = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (sec < 60) return `${sec} sec ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} hr ago`;

  const days = Math.floor(sec / 86400);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

function CategoryBadge({ category }) {
  if (!category?.theme) return null;
  return <span className={`badge badge--${category.theme}`}>{category.label}</span>;
}

function VisibleTags({ tags }) {
  const [showAllTags, setShowAllTags] = useState(false);
  const safeTags = splitTags(tags);
  const hiddenTagCount = Math.max(safeTags.length - INITIAL_VISIBLE_TAGS, 0);
  const visibleTags = showAllTags
    ? safeTags
    : safeTags.slice(0, INITIAL_VISIBLE_TAGS);

  useEffect(() => {
    setShowAllTags(false);
  }, [tags]);

  if (safeTags.length === 0) return null;

  return (
    <>
      {visibleTags.map((tag) => (
        <span key={tag} className="q-card__tag">{tag}</span>
      ))}
      {hiddenTagCount > 0 && (
        <button
          type="button"
          className="q-card__tag q-card__tag-toggle"
          onClick={(event) => {
            event.stopPropagation();
            setShowAllTags((prev) => !prev);
          }}
        >
          {showAllTags ? "less" : `+${hiddenTagCount} more`}
        </button>
      )}
    </>
  );
}

function FeedAccordionCard({ group, isOpen, onToggle, relatedGroups, onShowMoreRelated, onRelatedSelect }) {
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const visibleRelated = relatedGroups.slice(0, 2);
  const visiblePosts = showAllPosts
    ? group.posts
    : group.posts.slice(0, INITIAL_VISIBLE_POSTS);
  const { openRegister } = usePopup();

  useEffect(() => {
    if (!isOpen) {
      setShowAllPosts(false);
    }
  }, [isOpen]);

  const handleOpenReply = (event) => {
    event.stopPropagation();

    if (!group.questionId) return;

    if (!isLoggedIn()) {
      openRegister();
      return;
    }

    setShowReplyModal(true);
  };

  const handleSubmitReply = async (content) => {
    try {
      setReplySubmitting(true);

      await api.post(`/questions/${group.questionId}/reply`, {
        content,
      });

      setShowReplyModal(false);

      Swal.fire({
        icon: "success",
        title: "Reply Submitted",
        text: "Your reply is pending admin approval.",
      });
    } catch (error) {
      throw new Error(
        error?.response?.data?.errors?.content?.[0] ||
        error?.response?.data?.message ||
        "Unable to submit reply right now.",
      );
    } finally {
      setReplySubmitting(false);
    }
  };

  return (
    <div className={`q-card ${isOpen ? "q-card--open" : ""}`}>
      <div
        className="q-card__head"
        data-group-id={group.id}
        onClick={() => onToggle(group.id)}
      >
        <div className="q-card__body">
          <p className="q-card__text">{group.text}</p>
          <div className="q-card__meta">
            <CategoryBadge category={group.category} />
            <VisibleTags tags={group.tags} />
          </div>
        </div>
        <div className="q-card__stats">
          <svg className={`chevron ${isOpen ? "chevron--open" : ""}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <>
          {visiblePosts.length === 0 ? (
            <div className="q-card__feed">
              <div className="q-card__answers">
                <div className="q-card__post write-text">
                  Be the first one to Write
                </div>
              </div>
            </div>
          ) : (
            visiblePosts.map((post) => (
              <div key={post.id} className="q-card__feed padding-set">
                <div className="q-card__answers">
                  <div className="q-card__post">
                    <RealQuestionCard
                      question={post}
                      showActions
                      showDelete={false}
                      showEdit={false}
                      showCounts={false}
                    />
                  </div>
                </div>
              </div>
            ))
          )}

          {group.posts.length > INITIAL_VISIBLE_POSTS && (
            <div className="q-card__more-wrap">
              <button
                type="button"
                className="q-card__more-btn"
                onClick={() => setShowAllPosts((prev) => !prev)}
              >
                {showAllPosts
                  ? "Show fewer posts"
                  : `Show more posts (${group.posts.length - INITIAL_VISIBLE_POSTS})`}
              </button>
            </div>
          )}

          {group.questionId && (
            <div className="q-card__reply-wrap">
              <button
                type="button"
                className="q-card__reply-btn share_yours q-card__reply-btn--pulse"
                onClick={handleOpenReply}
              >
                Share yours...
              </button>
            </div>
          )}

          {relatedGroups.length > 0 && (
            <div className="q-card__feed">
              <div className="q-card__related">
                <div className="q-card__related-head">
                  <h3 className="q-card__related-title">Related posts</h3>
                  <span className="q-card__related-count">{relatedGroups.length}</span>
                </div>
                <div className="q-card__related-list">
                  {visibleRelated.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="q-card__related-item"
                      onClick={() => onRelatedSelect(item.id)}
                    >
                      <span className="q-card__related-text">{item.text}</span>
                      <span className="q-card__related-meta">
                        <CategoryBadge category={item.category} />
                      </span>
                    </button>
                  ))}
                </div>
                {relatedGroups.length > 2 && (
                  <div className="q-card__more-wrap q-card__more-wrap--related">
                    <button
                      type="button"
                      className="q-card__more-btn"
                      onClick={() => onShowMoreRelated(group)}
                    >
                      Show more
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ReplyModal
        open={showReplyModal}
        title="Reply to Question"
        helpText="Your reply will be reviewed by admin before it appears."
        submitting={replySubmitting}
        submitLabel="Submit Reply"
        onClose={() => setShowReplyModal(false)}
        onSubmit={handleSubmitReply}
      />
    </div>
  );
}

export default function DemoQuestionsFeedV2({
  items = [],
  title = "Real questions. Real stories",
}) {
  const [activeCat, setActiveCat] = useState("all");
  const [activeTag, setActiveTag] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [sort, setSort] = useState("new");
  const [showTopicsMenu, setShowTopicsMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_GROUPS);
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const topicsRef = useRef(null);
  const sortRef = useRef(null);

  const categories = useMemo(() => {
    const topicMap = new Map();

    items.forEach((item) => {
      const itemTopics = item.topic
        ? [item.topic]
        : (item.topics || item.posts?.flatMap((post) => post.topics || []) || []);

      itemTopics.forEach((topic) => {
        const id = normalizeGroupKey(topic.name || topic.slug || topic.id);
        if (!id || topicMap.has(id)) return;

        topicMap.set(id, {
          id,
          label: topic.name || topic.slug || String(topic.id ?? id),
          theme: CATEGORY_THEMES[topicMap.size % CATEGORY_THEMES.length],
        });
      });
    });

    return [{ id: "all", label: "All" }, ...Array.from(topicMap.values())];
  }, [items]);

  const topicFilterOptions = useMemo(() => {
    const findCategory = (...keys) =>
      categories.find((entry) => keys.includes(entry.id)) || null;

    const relationships = findCategory("relationships", "relationship");
    const historical = findCategory("historical monuments", "historical moments");
    const lifeEvents = findCategory("life events", "life event");
    const nature = findCategory("nature");
    const fashion = findCategory("fashion");

    return [
      relationships && { id: relationships.id, label: "Relationship" },
      historical && { id: historical.id, label: "Historical Monuments" },
      lifeEvents && { id: lifeEvents.id, label: "Life Events" },
      nature && { id: nature.id, label: "Nature" },
      fashion && { id: fashion.id, label: "Fashion" },
    ].filter(Boolean);
  }, [categories]);

  const groupedQuestions = useMemo(() => {
    return items.map((item, index) => {
      if (Array.isArray(item.posts) && item.question) {
        const topic = item.topic || item.posts?.[0]?.topics?.[0] || null;
        const cat = topic
          ? normalizeGroupKey(topic.name || topic.slug || topic.id)
          : "all";
        const category = categories.find((entry) => entry.id === cat) || null;
        const tags = (item.hashtags || "")
          .split(",")
          .map(normalizeTag)
          .filter(Boolean);
        const posts = [...item.posts].sort(
          (a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0),
        );
        const totalLikes = posts.reduce(
          (sum, post) =>
            sum + toNumber(
              post.likes_count ??
              post.total_likes ??
              post.likes?.filter((entry) => toNumber(entry?.is_like) === 1)?.length,
            ),
          0,
        );
        const totalDislikes = posts.reduce(
          (sum, post) =>
            sum + toNumber(
              post.dislikes_count ??
              post.total_dislikes ??
              post.likes?.filter((entry) => toNumber(entry?.is_like) === 0)?.length,
            ),
          0,
        );
        const totalFlags = posts.reduce((sum, post) => sum + toNumber(post.flags_count ?? post.flags?.length), 0);
        const totalViews = posts.reduce((sum, post) => sum + toNumber(post.totalview), 0);
        const replyCount = posts.length;
        const createdAt = item.updated_at || item.created_at || "";
        const createdAtValue = toTimeValue(createdAt);

        return {
          id: `question-${item.id ?? index}`,
          questionId: item.id ?? null,
          cat,
          category,
          text: item.question,
          tags,
          time: timeAgo(item.updated_at || item.created_at),
          popularity: totalLikes + replyCount * 2 + totalViews * 0.01 + toNumber(item.likes_count),
          controversy: totalDislikes + totalFlags + replyCount,
          createdAt,
          createdAtValue,
          posts,
        };
      }

      const firstTopic = item.topics?.[0] || null;
      const cat = firstTopic
        ? normalizeGroupKey(firstTopic.name || firstTopic.slug || firstTopic.id)
        : "all";
      const category = categories.find((entry) => entry.id === cat) || null;

      const likes = toNumber(item.likes_count);
      const dislikes = toNumber(item.dislikes_count);
      const flags = toNumber(item.flags_count ?? item.flags?.length);
      const views = toNumber(item.views_count || item.totalview);
      const createdAt = item.updated_at || item.created_at || "";
      const createdAtValue = toTimeValue(createdAt);

      return {
        id: `group-${cat}-${index}`,
        questionId: null,
        cat,
        category,
        text: item.title || stripHtml(item.description || "") || `Untitled post ${index + 1}`,
        tags: (item.slug || "").split(",").map(normalizeTag).filter(Boolean),
        time: timeAgo(item.updated_at || item.created_at),
        popularity: likes + views * 0.01,
        controversy: dislikes + flags,
        createdAt,
        createdAtValue,
        posts: [item],
      };
    });
  }, [items, categories]);

  const filteredGroups = useMemo(() => (
    groupedQuestions
      .filter((group) =>
        (!activeTag || group.tags.includes(activeTag))
      )
      .sort((a, b) => {
        if (activeCat !== "all") {
          const aSelected = a.cat === activeCat ? 1 : 0;
          const bSelected = b.cat === activeCat ? 1 : 0;
          if (aSelected !== bSelected) {
            return bSelected - aSelected;
          }
        }

        if (sort === "top") {
          return (
            b.popularity - a.popularity ||
            b.createdAtValue - a.createdAtValue ||
            b.controversy - a.controversy
          );
        }

        if (sort === "controversial") {
          return (
            b.controversy - a.controversy ||
            b.popularity - a.popularity ||
            b.createdAtValue - a.createdAtValue
          );
        }

        return b.createdAtValue - a.createdAtValue || b.popularity - a.popularity;
      })
  ), [groupedQuestions, activeCat, activeTag, sort]);

  const visibleGroups = useMemo(
    () => filteredGroups.slice(0, visibleCount),
    [filteredGroups, visibleCount],
  );

  useEffect(() => {
    setVisibleCount(filteredGroups.length || INITIAL_VISIBLE_GROUPS);
  }, [activeCat, activeTag, sort, items, filteredGroups.length]);

  useEffect(() => {
    if (visibleGroups.length === 0) {
      setOpenId(null);
      return;
    }

    setOpenId(visibleGroups[0].id);
  }, [visibleGroups]);

  useEffect(() => {
    if (!pendingScrollId) return;

    const targetCard = document.querySelector(`.q-card__head[data-group-id="${pendingScrollId}"]`);
    if (!targetCard) return;

    targetCard.scrollIntoView({ behavior: "smooth", block: "start" });
    setPendingScrollId(null);
  }, [pendingScrollId, visibleGroups, openId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (topicsRef.current && !topicsRef.current.contains(event.target)) {
        setShowTopicsMenu(false);
      }

      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const relatedByGroupId = useMemo(() => {
    return groupedQuestions.reduce((acc, group) => {
      acc[group.id] = groupedQuestions
        .filter((candidate) => candidate.id !== group.id)
        .filter((candidate) => candidate.cat === group.cat || candidate.tags.some((tag) => group.tags.includes(tag)))
        .sort((a, b) => {
          const tagOverlapA = a.tags.filter((tag) => group.tags.includes(tag)).length;
          const tagOverlapB = b.tags.filter((tag) => group.tags.includes(tag)).length;
          return tagOverlapB - tagOverlapA || b.popularity - a.popularity;
        });
      return acc;
    }, {});
  }, [groupedQuestions]);

  const handleShowMoreRelated = (group) => {
    setActiveCat(group.cat);
    setVisibleCount(INITIAL_VISIBLE_GROUPS);
    setOpenId(group.id);
    setShowTopicsMenu(false);
    setShowSortMenu(false);
  };

  const handleRelatedSelect = (targetId) => {
    const targetIndex = filteredGroups.findIndex((group) => group.id === targetId);

    if (targetIndex >= 0) {
      setVisibleCount((currentCount) => Math.max(currentCount, targetIndex + 1));
    }

    setOpenId(targetId);
    setShowTopicsMenu(false);
    setShowSortMenu(false);
    setPendingScrollId(targetId);
  };

  const sortLabel =
    sort === "new" ? "Newest" : sort === "top" ? "Top" : "Controversial";

  return (
    <div className="questions-feed  notranslate" translate="no">
      <div className="questions-feed__header">
        <div>
          <h2 className="questions-feed__title">
            {title}
          </h2>
        </div>
        <div className="sort-toggle">
          <div className="questions-feed__filters" ref={topicsRef}>
            <div className="filter-dropdown">
              <button
                type="button"
                className="filter-toggle-btn"
                onClick={() => {
                  setShowTopicsMenu((prev) => !prev);
                  setShowSortMenu(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 5h18M6 12h12M10 19h4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Topics
              </button>
            </div>

            <div
              className={`cat-pills ${showTopicsMenu ? "cat-pills--open" : "cat-pills--hidden"}`}
              aria-hidden={!showTopicsMenu}
            >
              {topicFilterOptions.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`cat-pill ${activeCat === category.id ? "cat-pill--active" : ""}`}
                  onClick={() => {
                    setActiveCat(category.id);
                    setOpenId(null);
                    setShowTopicsMenu(false);
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          <div className="questions-feed__filters" ref={sortRef}>
            <div className="filter-dropdown">
              <button
                type="button"
                className="filter-toggle-btn"
                onClick={() => {
                  setShowSortMenu((prev) => !prev);
                  setShowTopicsMenu(false);
                }}
              >
                Filter: {sortLabel}
              </button>
            </div>

            <div
              className={`cat-pills ${showSortMenu ? "cat-pills--open" : "cat-pills--hidden"}`}
              aria-hidden={!showSortMenu}
            >
              {["new", "top", "controversial"].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`cat-pill ${sort === type ? "cat-pill--active" : ""}`}
                  onClick={() => {
                    setSort(type);
                    setShowSortMenu(false);
                  }}
                >
                  {type === "new" ? "Newest" : type === "top" ? "Top" : "Controversial"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="questions-feed__list">
        {visibleGroups.length === 0 ? (
          <div className="questions-feed__empty">No questions match this filter.</div>
        ) : (
          visibleGroups.map((group) => (
            <FeedAccordionCard
              key={group.id}
              group={group}
              isOpen={openId === group.id}
              relatedGroups={relatedByGroupId[group.id] || []}
              onRelatedSelect={handleRelatedSelect}
              onToggle={(targetId) => setOpenId((currentOpenId) => (
                currentOpenId === targetId ? null : targetId
              ))}
              onShowMoreRelated={handleShowMoreRelated}
            />
          ))
        )}
      </div>

      {filteredGroups.length > visibleCount && (
        <div className="q-card__more-wrap questions-feed__more">
          <button
            type="button"
            className="q-card__more-btn"
            onClick={() => setVisibleCount((count) => count + INITIAL_VISIBLE_GROUPS)}
          >
            Show more
          </button>
        </div>
      )}
    </div>
  );
}
